import {useTheme} from '@mui/material';
import {useBeneficiaryCard, usePersonalData} from 'presentation/hooks';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {formatText, getUserInfoFromToken} from 'presentation/utils';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {useBookingTheme} from 'presentation/modules/booking/shared/hooks/useBookingTheme';
import {useIsAdvanceDiagnosticsExamsJourney} from 'presentation/modules/booking/exam/hooks/useIsAdvanceDiagnosticsExamsJourney';
import {getExamStepPosition} from 'presentation/modules/booking/exam/utils/getExamStepPosition';
import {translations} from 'presentation/translations-new';
import {UseExamSummarySectionGroupProps} from './types';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useNavigate} from 'react-router-dom';
import {useResetBookingFlow} from 'presentation/modules/booking/shared/hooks/useResetBookingFlow';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {ExamBookingEnum} from 'presentation/constants';
import {useSummaryChangeDrawerStore} from 'presentation/modules/booking/shared/hooks/useBookingSummary/store/useSummaryChangeDrawerStore';

const {usingYourLocation} = translations['pt-br'].commons;

export function useExamSummarySectionGroup({
	activeStep,
	handleChangeModals,
}: UseExamSummarySectionGroupProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {beneficiaryCardPlanData} = useBeneficiaryCard();
	const {currentEmail, currentPhone} = usePersonalData();
	const bookingTheme = useBookingTheme({flowType: FlowEnum.EXAM});
	const {colors} = useTheme();
	const activeBookingFlowStep = useBookingFlowStepsStore(state => state.bookingStep);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const navigate = useNavigate();
	const {resetBookingFlow} = useResetBookingFlow();
	const setOpenDrawerState = useSummaryChangeDrawerStore(state => state.setSummaryChangeDrawer);
	const isAdvanceDiagnosticsFlow = useIsAdvanceDiagnosticsExamsJourney();

	const {
		isRebooking,
		unitName,
		unitAddress,
		specialtyCode,
		specialtyName,
		isManualLocation,
		districtName,
	} = bookingData;

	const isUsingGeolocation = !isManualLocation;

	const {cdUsuario} = getUserInfoFromToken();
	const {UNIT_STEP, SPECIALITY_OR_EXAM_STEP, LOCATION_STEP, REVIEW_STEP} = getExamStepPosition({
		isManualLocation,
	});
	const isLastStep = activeStep === REVIEW_STEP;

	const showSpecialtyField = activeStep > 1 && !isLastStep;
	const isAdvanceDiagnosticsExamStep =
		isAdvanceDiagnosticsFlow && activeStep === SPECIALITY_OR_EXAM_STEP;
	const showLocationField = !isAdvanceDiagnosticsFlow;
	const showUnitField =
		isAdvanceDiagnosticsExamStep ||
		(!isLastStep && isManualLocation ? activeStep > 3 : activeStep > 2);
	const showDistricsField = isManualLocation && activeStep === UNIT_STEP && districtName;

	const getLocationPrimaryText = () => {
		if (isUsingGeolocation) {
			return usingYourLocation;
		}

		return formatText(bookingData.cityName);
	};

	const getLocationSecondaryText = () => {
		if (isUsingGeolocation) return undefined;

		return formatText(bookingData.ufName);
	};

	const handleChangeLocation = () => {
		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			resetBookingFlow();
			setOpenDrawerState(false);

			if (bookingData.isManualLocation) {
				setActiveBookingFlowStep(LOCATION_STEP);
				return;
			}

			navigate(ExamBookingEnum.EXAM_BOOKING_UF, {replace: true});
			setActiveBookingFlowStep(LOCATION_STEP);
			return;
		}

		handleChangeModals(ChangeFlowVariantEnum.LOCATION);
	};

	return {
		showSpecialtyField,
		showLocationField,
		showUnitField,
		getLocationPrimaryText,
		getLocationSecondaryText,
		showDistricsField,
		bookingTheme,
		colors,
		cdUsuario,
		beneficiaryCardPlanData,
		currentEmail,
		currentPhone,
		isLastStep,
		isUsingGeolocation,
		isRebooking,
		hideChangeActions: Boolean(isRebooking) || isAdvanceDiagnosticsFlow,
		hideSpecialtyChangeAction: Boolean(isRebooking),
		unitName,
		unitAddress,
		specialtyCode,
		specialtyName,
		handleChangeLocation,
	};
}
